import React from 'react';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import MEL from '../../assets/images/melo.png'; // icône email (optionnelle)

// Palette dorée
const colors = {
  goldLight: '#F5E1C8',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  whiteGlass: 'rgba(255, 255, 255, 0.7)',
  whiteGlassHover: 'rgba(255, 255, 255, 0.85)',
  borderGlass: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.08)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

// Variantes d'animation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const SubscribeSection = () => {
  return (
    <>
      <style>{`
        .subscribe-section {
          position: relative;
          padding: 80px 0;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradient 15s ease infinite;
          overflow: hidden;
        }

        @keyframes goldGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Orbes décoratives floues */
        .subscribe-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.4;
          z-index: 0;
        }
        .orb-left {
          width: 45vw;
          height: 45vw;
          background: #F5E1C8;
          top: -15%;
          left: -10%;
          animation: floatOrb 20s ease-in-out infinite;
        }
        .orb-right {
          width: 35vw;
          height: 35vw;
          background: #E8CFB5;
          bottom: -10%;
          right: -5%;
          animation: floatOrb 18s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
        }

        /* Cartes glassmorphism */
        .contact-card {
          background: ${colors.whiteGlass};
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid ${colors.borderGlass};
          border-radius: 28px;
          padding: 40px 30px;
          box-shadow: ${colors.shadow};
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .contact-card:hover {
          background: ${colors.whiteGlassHover};
          box-shadow: ${colors.shadowHover};
          transform: translateY(-4px);
          border-color: ${colors.gold};
        }

        .card-icon {
          font-size: 2.5rem;
          color: ${colors.gold};
          margin-bottom: 20px;
          filter: drop-shadow(0 4px 8px rgba(198, 156, 109, 0.3));
        }

        .card-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 10px;
          letter-spacing: -0.3px;
        }

        .card-subtitle {
          font-size: 1.2rem;
          color: ${colors.textSoft};
          margin-bottom: 20px;
          font-weight: 400;
        }

        .card-link {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: 2px solid ${colors.gold};
          color: ${colors.goldDark};
          padding: 12px 28px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1.05rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(5px);
        }

        .card-link:hover {
          background: ${colors.gold};
          color: #fff;
          border-color: ${colors.gold};
          transform: scale(1.03);
          box-shadow: 0 8px 20px rgba(198, 156, 109, 0.3);
        }

        .card-link i, .card-link svg {
          font-size: 1.2rem;
        }

        /* Image icône email */
        .email-icon-img {
          width: 70px;
          height: 70px;
          object-fit: contain;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1));
          margin-bottom: 15px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .subscribe-section {
            padding: 60px 0;
          }
          .contact-card {
            padding: 30px 20px;
            margin-bottom: 20px;
          }
        }
      `}</style>

      <section className="subscribe-section">
        {/* Orbes dorées */}
        <div className="subscribe-orb orb-left"></div>
        <div className="subscribe-orb orb-right"></div>

        <div className="container">
          <motion.div
            className="row g-4 align-items-stretch"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {/* Colonne gauche - Téléphone */}
            <div className="col-lg-6">
              <motion.div className="contact-card" variants={itemVariants}>
                <FaPhoneAlt className="card-icon" />
                <h3 className="card-title">Contactez-nous</h3>
                <p className="card-subtitle">Un conseiller à votre écoute</p>
                <a href="tel:+243835593568" className="card-link">
                  <FaPhoneAlt /> (+243) 835-593-568
                </a>
              </motion.div>
            </div>

            {/* Colonne droite - Email */}
            <div className="col-lg-6">
              <motion.div className="contact-card" variants={itemVariants}>
                {/* Icône personnalisée (image) */}
                <img src={MEL} alt="Email" className="email-icon-img" />
                <h3 className="card-title">Écrivez-nous</h3>
                <p className="card-subtitle">Pour plus d'informations</p>
                <a href="mailto:infos@azyekos-services.com" className="card-link">
                  <FaEnvelope /> infos@azyekos-services.com
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SubscribeSection;